import { useState, useEffect } from "react";
import { Loader2 } from "lucide-react";
import { useInternalUsers } from "@/features/tasks/hooks/useInternalUsers";
import { UserAvatar } from "@/components/ui/UserAvatar";
import { useUpdateResource } from "../hooks/useRepository";
import type { ResourceManager } from "../types";

interface Props {
  resourceId: string;
  managers: ResourceManager[];
}

export function ManagersPanel({ resourceId, managers }: Props) {
  const { data: allUsers = [] } = useInternalUsers();
  const { mutateAsync: updateResource, isPending } = useUpdateResource();

  const [selectedIds, setSelectedIds] = useState<string[]>(managers.map((m) => m.id));

  useEffect(() => {
    setSelectedIds(managers.map((m) => m.id));
  }, [managers]);

  const internalNonDirectors = allUsers.filter((u) => u.role !== "contractor" && u.role !== "director");

  function toggle(id: string) {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }

  async function handleSave() {
    await updateResource({ id: resourceId, data: { manager_ids: selectedIds } });
  }

  return (
    <div className="flex flex-col gap-3">
      <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
        Zarządzający dokumentem
      </p>
      <p className="text-xs text-gray-400">
        Wybrani użytkownicy mogą wgrywać nowe wersje pliku (oprócz dyrektora).
      </p>

      <div className="max-h-56 overflow-y-auto flex flex-col gap-1 p-2">
        {internalNonDirectors.map((u) => (
          <label key={u.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 rounded px-1 py-0.5">
            <input
              type="checkbox"
              checked={selectedIds.includes(u.id)}
              onChange={() => toggle(u.id)}
              className="accent-[var(--color-primary)]"
            />
            <UserAvatar fullName={u.full_name} profilePictureUrl={u.profile_picture_url} size="xs" />
            <span className="text-gray-700 dark:text-gray-300">{u.full_name}</span>
            <span className="text-xs text-gray-400 ml-auto">{u.role}</span>
          </label>
        ))}
      </div>

      <button
        onClick={handleSave}
        disabled={isPending}
        className="self-end flex items-center gap-1.5 px-3 py-1.5 bg-[var(--color-primary)] text-white text-sm rounded-lg hover:bg-[var(--color-primary-hover)] disabled:opacity-50"
      >
        {isPending && <Loader2 size={13} className="animate-spin" />}
        Zapisz
      </button>
    </div>
  );
}
